/* Home, the Reception "Today" panel and the Settings overview
   (docs/plans/phase1-home-and-demo-data.md §3). Tokens only; flat surfaces. */

/* The home cards fill the content panel: two per row when there is room (ADR 0019). */
.page {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;
  gap: var(--space-5);
}

@container (width >= 720px) {
  .page {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/* A home card: a primary accent bar along the top edge, so the page feels alive (ADR 0028). */
.page > .section {
  border-top: 3px solid var(--brand);
}

.sectionHeading {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font: var(--t-heading);
}

/* The section's line icon in a primary-tint chip. Decoration: the title names the card. */
.chip {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-control);
  background: var(--brand-tint);
  color: var(--brand);
}

.summary {
  max-width: 75ch;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.muted {
  color: var(--graphite);
  font: var(--t-small);
}

/* A plain list with rule lines between rows: checklist, stock alerts, settings areas. */
.list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--rule);
  list-style: none;
  container-type: inline-size;
}

/* First thing in its card: the card's edge already separates it. */
.list:first-child {
  border-top: 0;
}

.listRow {
  display: grid;
  grid-template-columns: minmax(12rem, 16rem) minmax(0, 1fr) auto;
  gap: var(--space-2) var(--space-4);
  align-items: baseline;
  min-height: var(--row-height);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--rule);
}

.listRow > :last-child {
  justify-self: end;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.listLabel {
  font: var(--t-body-strong);
}

/* In a narrow card (two cards side by side at 1366px) the detail goes under the label
   instead of being squeezed into a column a few words wide. */
@container (width < 40rem) {
  .listRow {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .listRow > :nth-child(2) {
    grid-column: 1 / -1;
    grid-row: 2;
  }
}

/* Today's arrivals: 48px rows like the queue list (DESIGN.md §6.3). */
.todayList {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--rule);
  list-style: none;
}

.todayRow {
  display: grid;
  grid-template-columns: 3.25rem minmax(0, 1fr) auto;
  gap: 0 var(--space-3);
  align-items: start;
  min-height: 48px;
  padding: var(--space-2) var(--space-2);
  border-bottom: 1px solid var(--rule);
  color: var(--ink);
  text-decoration: none;
}

.todayRow:hover {
  background: var(--brand-tint);
}

.todayRow:hover .todayName {
  text-decoration: underline;
}

.todayTime {
  font: var(--t-body);
  font-variant-numeric: tabular-nums;
}

/* On a phone the state tag moves under the name so the name keeps the full width. */
@media (width < 480px) {
  .todayRow {
    grid-template-columns: 3.25rem minmax(0, 1fr);
    row-gap: var(--space-1);
  }

  .todayRow > :last-child {
    grid-column: 2;
    justify-self: start;
  }
}

.todayWho {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.todayName {
  font: var(--t-body-strong);
  overflow-wrap: anywhere;
}

.todayMeta {
  display: flex;
  flex-wrap: wrap;
  gap: 0 var(--space-3);
  color: var(--graphite);
  font: var(--t-small);
}

.todayNumber {
  font-family: var(--font-id);
}

/* Reception: search and check-in on the left, today's list on the right. */
.reception {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: var(--space-6);
  align-items: start;
}

.receptionMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
}

.todayPanel {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.panelHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

@media (width < 1100px) {
  .reception {
    grid-template-columns: minmax(0, 1fr);
  }
}
